/**
 * KeywordIcons — Renders keyword icons for card keywords (mana-font icon or generated glyph).
 *
 * Two visual tiers:
 * - **Evergreen** (emerald tint) — 15 universally-known abilities
 * - **Other** (amber tint) — all remaining supported keywords
 *
 * Supports icon-only mode (default, for table columns) and icon+text mode (for CardShowcase pills).
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';

import { getCategoryStyle, getKeywordIcons, type KeywordIconEntry } from '@/lib/keywordIcons';

import KeywordAbilityIcon from './KeywordAbilityIcon';

// ============================================================================
// Types
// ============================================================================

export interface KeywordIconsProps {
  /** Raw keywords array from card data */
  keywords: string[];
  /** Maximum visible icons before showing +N overflow (default: 5) */
  maxVisible?: number;
  /** Icon-only mode (default: true). When false, renders icon+text pills. */
  iconOnly?: boolean;
  /** Additional class names for the container */
  className?: string;
}

// ============================================================================
// Subcomponents
// ============================================================================

/** Single keyword icon in a tinted pill (mirrors RoleTag icon-only) */
function KeywordIcon({ entry }: { entry: KeywordIconEntry }): ReactElement {
  const style = getCategoryStyle(entry.category);
  return (
    <span className={clsx('inline-flex shrink-0 items-center rounded-full px-1 py-0.5', style.bg)} title={entry.keyword}>
      <KeywordAbilityIcon def={entry} color={style.color} />
    </span>
  );
}

/** Icon+text pill for CardShowcase (mirrors RoleTag) */
function KeywordPill({ entry }: { entry: KeywordIconEntry }): ReactElement {
  const style = getCategoryStyle(entry.category);
  return (
    <span className={clsx('inline-flex items-center gap-1 rounded-full px-1 py-0.5', style.pill)} title={entry.keyword}>
      <KeywordAbilityIcon def={entry} color={style.color} />
      <span className="text-[10px] font-medium">{entry.keyword}</span>
    </span>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function KeywordIcons({ keywords, maxVisible = 5, iconOnly = true, className }: KeywordIconsProps): ReactElement | null {
  const entries = getKeywordIcons(keywords);

  if (entries.length === 0) return null;

  const visible = entries.slice(0, maxVisible);
  const overflow = entries.length - maxVisible;
  const overflowTooltip =
    overflow > 0
      ? entries
          .slice(maxVisible)
          .map((e) => e.keyword)
          .join(', ')
      : undefined;

  if (!iconOnly) {
    // Icon+text pill layout (for CardShowcase)
    return (
      <div className={clsx('flex flex-wrap items-center gap-1', className)}>
        {visible.map((entry) => (
          <KeywordPill key={entry.keyword} entry={entry} />
        ))}
        {overflow > 0 && (
          <span
            className="inline-flex items-center justify-center rounded-full bg-zinc-700 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400"
            title={overflowTooltip}
          >
            +{overflow}
          </span>
        )}
      </div>
    );
  }

  // Icon-only layout (for table columns)
  return (
    <div className={clsx('flex flex-nowrap items-center gap-1', className)}>
      {visible.map((entry) => (
        <KeywordIcon key={entry.keyword} entry={entry} />
      ))}
      {overflow > 0 && (
        <span
          className="inline-flex shrink-0 items-center justify-center rounded-full bg-zinc-700 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400"
          title={overflowTooltip}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
